import React, { useCallback, useEffect, useState, useRef } from 'react';
interface FadeInProps {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  className?: string;
}
const transforms: Record<string, string> = {
  up: 'translateY(30px)',
  down: 'translateY(-30px)',
  left: 'translateX(30px)',
  right: 'translateX(-30px)',
  none: 'translate(0)'
};
// Single shared IntersectionObserver for all FadeIn instances
let sharedObserver: IntersectionObserver | null = null;
const callbacks = new Map<Element, () => void>();
function getObserver(): IntersectionObserver {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const cb = callbacks.get(entry.target);
          if (cb) {
            cb();
            callbacks.delete(entry.target);
            sharedObserver?.unobserve(entry.target);
          }
        }
      });
    }, {
      rootMargin: '-60px'
    });
  }
  return sharedObserver;
}
export function FadeIn({
  children,
  delay = 0,
  direction = 'up',
  className = ''
}: FadeInProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = getObserver();
    callbacks.set(el, () => setIsVisible(true));
    observer.observe(el);
    return () => {
      callbacks.delete(el);
      observer.unobserve(el);
    };
  }, []);
  return <div ref={ref} className={className} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translate(0)' : transforms[direction],
    transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`
  }} data-id="element-448">
      {children}
    </div>;
}